import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  Users, BookOpen, FileText, Calendar, Bell, Video, 
  BarChart3, Clock, CheckCircle, AlertCircle, LogOut,
  ChevronRight, UserCheck, UserX, Send, Plus, Eye, Trash2, Edit2, X,
  MessageSquare, User, Trophy, Star, Medal, Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [students, setStudents] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [reports, setReports] = useState(null);
  const [directMessages, setDirectMessages] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [selectedStudentGamification, setSelectedStudentGamification] = useState(null);
  
  // Modals
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showLiveClassModal, setShowLiveClassModal] = useState(false);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showGamificationModal, setShowGamificationModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // Form states
  const [announcementForm, setAnnouncementForm] = useState({ title: '', message: '', priority: 'normal' });
  const [liveClassForm, setLiveClassForm] = useState({ title: '', description: '', course_id: '', scheduled_at: '', duration_minutes: 60, meeting_link: '' });
  const [gradeForm, setGradeForm] = useState({ grade: '', feedback: '', status: 'graded' });
  const [messageForm, setMessageForm] = useState({ subject: '', message: '' });
  const [replyText, setReplyText] = useState('');

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, studentsRes, subsRes, attRes, annRes, classRes, msgRes] = await Promise.all([
        axios.get(`${API}/teacher/dashboard`, { withCredentials: true }),
        axios.get(`${API}/teacher/students`, { withCredentials: true }),
        axios.get(`${API}/teacher/submissions`, { withCredentials: true }),
        axios.get(`${API}/teacher/attendance/today`, { withCredentials: true }),
        axios.get(`${API}/teacher/announcements`, { withCredentials: true }),
        axios.get(`${API}/teacher/live-classes`, { withCredentials: true }),
        axios.get(`${API}/teacher/messages`, { withCredentials: true })
      ]);
      setDashboard(dashRes.data);
      setStudents(studentsRes.data);
      setSubmissions(subsRes.data);
      setAttendance(attRes.data);
      setAnnouncements(annRes.data);
      setLiveClasses(classRes.data);
      setDirectMessages(msgRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/teacher/login');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/teacher/login');
    } else if (user && user.role !== 'teacher') {
      navigate('/');
    } else if (user) {
      fetchDashboard();
    }
  }, [user, authLoading, navigate, fetchDashboard]);

  const fetchReports = async () => {
    try {
      const [attendanceReport, progressReport] = await Promise.all([
        axios.get(`${API}/teacher/reports/attendance`, { withCredentials: true }),
        axios.get(`${API}/teacher/reports/progress`, { withCredentials: true })
      ]);
      setReports({ attendance: attendanceReport.data, progress: progressReport.data });
    } catch (error) {
      toast.error('Failed to load reports');
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/teacher/leaderboard`, { withCredentials: true });
      setLeaderboard(res.data);
    } catch (error) {
      toast.error('Failed to load leaderboard');
    }
  };

  const fetchStudentGamification = async (studentId) => {
    try {
      const res = await axios.get(`${API}/teacher/student/${studentId}/gamification`, { withCredentials: true });
      setSelectedStudentGamification(res.data);
      setShowGamificationModal(true);
    } catch (error) {
      toast.error('Failed to load student gamification');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/teacher/login');
  };

  const signInStudent = async (studentId) => {
    try {
      await axios.post(`${API}/teacher/attendance/sign-in`, { student_id: studentId }, { withCredentials: true });
      toast.success('Student signed in');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to sign in student');
    }
  };

  const signOutStudent = async (studentId) => {
    try {
      await axios.post(`${API}/teacher/attendance/sign-out`, { student_id: studentId }, { withCredentials: true });
      toast.success('Student signed out');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign out');
    }
  };

  const createAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/teacher/announcements`, announcementForm, { withCredentials: true });
      toast.success('Announcement sent!');
      setShowAnnouncementModal(false);
      setAnnouncementForm({ title: '', message: '', priority: 'normal' });
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const createLiveClass = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/teacher/live-classes`, {
        ...liveClassForm,
        course_id: dashboard?.courses?.[0]?.id || 'general'
      }, { withCredentials: true });
      toast.success('Live class scheduled!');
      setShowLiveClassModal(false);
      setLiveClassForm({ title: '', description: '', course_id: '', scheduled_at: '', duration_minutes: 60, meeting_link: '' });
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to schedule class');
    }
  };

  const gradeSubmission = async (e) => {
    e.preventDefault();
    if (!selectedSubmission) return;
    try {
      await axios.put(`${API}/teacher/submissions/${selectedSubmission.id}/grade`, {
        grade: parseInt(gradeForm.grade),
        feedback: gradeForm.feedback,
        status: gradeForm.status
      }, { withCredentials: true });
      toast.success('Submission graded!');
      setShowGradeModal(false);
      setSelectedSubmission(null);
      setGradeForm({ grade: '', feedback: '', status: 'graded' });
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to grade submission');
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API}/teacher/announcements/${id}`, { withCredentials: true });
      toast.success('Announcement deleted');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const deleteLiveClass = async (id) => {
    if (!window.confirm('Delete this live class?')) return;
    try {
      await axios.delete(`${API}/teacher/live-classes/${id}`, { withCredentials: true });
      toast.success('Live class deleted');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Direct Messaging Functions
  const sendDirectMessage = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;
    try {
      await axios.post(`${API}/teacher/messages`, {
        student_id: selectedStudent.student.id,
        subject: messageForm.subject,
        message: messageForm.message
      }, { withCredentials: true });
      toast.success('Message sent!');
      setShowMessageModal(false);
      setMessageForm({ subject: '', message: '' });
      setSelectedStudent(null);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const viewMessageThread = async (thread) => {
    try {
      const res = await axios.get(`${API}/teacher/messages/${thread.id}`, { withCredentials: true });
      setSelectedThread(res.data);
    } catch (error) {
      toast.error('Failed to load message');
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!selectedThread || !replyText.trim()) return;
    try {
      await axios.post(`${API}/teacher/messages/${selectedThread.id}/reply`, {
        message: replyText
      }, { withCredentials: true });
      toast.success('Reply sent!');
      setReplyText('');
      viewMessageThread(selectedThread);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AEEF]"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Welcome, {user?.name}</h1>
            <p className="text-[#94A3B8]">Teacher Dashboard</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="teacher-logout-btn"
            className="flex items-center gap-2 bg-[#0A1F44] text-[#94A3B8] hover:text-white px-4 py-2 rounded-xl border border-[#00AEEF]/20 hover:border-[#FF6A00] transition-all"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
            <Users className="w-6 h-6 text-[#00AEEF] mb-2" />
            <p className="text-2xl font-bold text-white">{students.length}</p>
            <p className="text-xs text-[#94A3B8]">Students</p>
          </div>
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
            <FileText className="w-6 h-6 text-[#FF6A00] mb-2" />
            <p className="text-2xl font-bold text-white">{dashboard?.pending_submissions || 0}</p>
            <p className="text-xs text-[#94A3B8]">To Grade</p>
          </div>
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
            <CheckCircle className="w-6 h-6 text-[#00FFE0] mb-2" />
            <p className="text-2xl font-bold text-white">{dashboard?.attendance_today?.present || 0}</p>
            <p className="text-xs text-[#94A3B8]">Present Today</p>
          </div>
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
            <AlertCircle className="w-6 h-6 text-red-400 mb-2" />
            <p className="text-2xl font-bold text-white">{dashboard?.attendance_today?.absent || 0}</p>
            <p className="text-xs text-[#94A3B8]">Absent Today</p>
          </div>
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
            <BookOpen className="w-6 h-6 text-purple-400 mb-2" />
            <p className="text-2xl font-bold text-white">{dashboard?.courses?.length || 0}</p>
            <p className="text-xs text-[#94A3B8]">Courses</p>
          </div>
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-4">
            <Video className="w-6 h-6 text-yellow-400 mb-2" />
            <p className="text-2xl font-bold text-white">{liveClasses.filter(c => c.status === 'scheduled').length}</p>
            <p className="text-xs text-[#94A3B8]">Upcoming Classes</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#00AEEF]/20 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'students', label: 'Students' },
            { id: 'leaderboard', label: 'Leaderboard' },
            { id: 'messages', label: 'Messages' },
            { id: 'submissions', label: 'Submissions' },
            { id: 'attendance', label: 'Attendance' },
            { id: 'announcements', label: 'Announcements' },
            { id: 'live-classes', label: 'Live Classes' },
            { id: 'reports', label: 'Reports' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'reports' && !reports) fetchReports();
                if (tab.id === 'leaderboard' && !leaderboard) fetchLeaderboard();
              }}
              data-testid={`teacher-tab-${tab.id}`}
              className={`px-6 py-3 font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-[#00FFE0] border-b-2 border-[#00FFE0]'
                  : 'text-[#94A3B8] hover:text-white'
              }`}
            >
              {tab.label}
              {tab.id === 'messages' && directMessages.some(m => m.messages?.some(msg => msg.sender === 'student' && !msg.read)) && (
                <span className="ml-2 w-2 h-2 bg-[#FF6A00] rounded-full inline-block"></span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pending Submissions */}
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Pending Submissions</h2>
                <button onClick={() => setActiveTab('submissions')} className="text-[#00AEEF] text-sm hover:text-[#00FFE0]">
                  View All
                </button>
              </div>
              {submissions.filter(s => s.status === 'submitted').slice(0, 5).map((sub) => (
                <div key={sub.id} className="flex items-center justify-between py-3 border-b border-[#00AEEF]/10 last:border-0">
                  <div>
                    <p className="text-white font-medium">{sub.student_name}</p>
                    <p className="text-[#94A3B8] text-sm">{new Date(sub.submitted_at).toLocaleDateString()}</p>
                  </div>
                  <button
                    onClick={() => { setSelectedSubmission(sub); setShowGradeModal(true); }}
                    className="text-[#FF6A00] text-sm hover:text-[#FF6A00]/80"
                  >
                    Grade
                  </button>
                </div>
              ))}
              {submissions.filter(s => s.status === 'submitted').length === 0 && (
                <p className="text-[#94A3B8] text-center py-4">No pending submissions</p>
              )}
            </div>

            {/* Upcoming Live Classes */}
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Upcoming Classes</h2>
                <button
                  onClick={() => setShowLiveClassModal(true)}
                  className="flex items-center gap-1 text-[#00FFE0] text-sm hover:text-[#00FFE0]/80"
                >
                  <Plus className="w-4 h-4" /> Schedule
                </button>
              </div>
              {liveClasses.filter(c => c.status === 'scheduled').slice(0, 5).map((lc) => (
                <div key={lc.id} className="flex items-center justify-between py-3 border-b border-[#00AEEF]/10 last:border-0">
                  <div>
                    <p className="text-white font-medium">{lc.title}</p>
                    <p className="text-[#94A3B8] text-sm">{new Date(lc.scheduled_at).toLocaleString()}</p>
                  </div>
                  {lc.meeting_link && (
                    <a href={lc.meeting_link} target="_blank" rel="noopener noreferrer" className="text-[#00AEEF] text-sm">
                      Join
                    </a>
                  )}
                </div>
              ))}
              {liveClasses.filter(c => c.status === 'scheduled').length === 0 && (
                <p className="text-[#94A3B8] text-center py-4">No upcoming classes</p>
              )}
            </div>

            {/* Recent Announcements */}
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Recent Announcements</h2>
                <button
                  onClick={() => setShowAnnouncementModal(true)}
                  className="flex items-center gap-1 text-[#FF6A00] text-sm hover:text-[#FF6A00]/80"
                >
                  <Plus className="w-4 h-4" /> New
                </button>
              </div>
              {announcements.slice(0, 3).map((ann) => (
                <div key={ann.id} className="py-3 border-b border-[#00AEEF]/10 last:border-0">
                  <p className="text-white font-medium">{ann.title}</p>
                  <p className="text-[#94A3B8] text-sm line-clamp-2">{ann.message}</p>
                </div>
              ))}
              {announcements.length === 0 && (
                <p className="text-[#94A3B8] text-center py-4">No announcements yet</p>
              )}
            </div>

            {/* Quick Attendance */}
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Today's Attendance</h2>
                <button onClick={() => setActiveTab('attendance')} className="text-[#00AEEF] text-sm hover:text-[#00FFE0]">
                  Manage
                </button>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-[#060F22]/50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-[#00FFE0]">{dashboard?.attendance_today?.present || 0}</p>
                  <p className="text-xs text-[#94A3B8]">Present</p>
                </div>
                <div className="bg-[#060F22]/50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-red-400">{dashboard?.attendance_today?.absent || 0}</p>
                  <p className="text-xs text-[#94A3B8]">Absent</p>
                </div>
                <div className="bg-[#060F22]/50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-white">{dashboard?.attendance_today?.total || 0}</p>
                  <p className="text-xs text-[#94A3B8]">Total</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#060F22]">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Student</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Level</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Points</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Progress</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Quiz Avg</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#00AEEF]/10">
                {students.map((s) => (
                  <tr key={s.student.id || s.student.email} className="hover:bg-[#060F22]/50">
                    <td className="px-4 py-3">
                      <p className="text-white font-medium">{s.student.name}</p>
                      <p className="text-[#94A3B8] text-sm">{s.student.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[#00FFE0] font-medium">
                        {s.student.total_points >= 1000 ? 'Architect' :
                         s.student.total_points >= 600 ? 'Engineer' :
                         s.student.total_points >= 300 ? 'Developer' :
                         s.student.total_points >= 100 ? 'Apprentice' : 'Beginner'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Zap className="w-4 h-4 text-[#FF6A00]" />
                        <span className="text-white">{s.student.total_points || 0}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-white">{s.stats.completed_lessons} lessons</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-white">{s.stats.quiz_average}%</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => fetchStudentGamification(s.student.id)}
                          className="flex items-center gap-1 text-[#FFD700] hover:text-[#FFD700]/80 text-sm"
                          data-testid={`view-gamification-${s.student.id}`}
                        >
                          <Trophy className="w-4 h-4" />
                          Stats
                        </button>
                        <button
                          onClick={() => { setSelectedStudent(s); setShowMessageModal(true); }}
                          className="flex items-center gap-1 text-[#00AEEF] hover:text-[#00FFE0] text-sm"
                        >
                          <MessageSquare className="w-4 h-4" />
                          Message
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Message Threads List */}
            <div className="lg:col-span-1 bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-[#00AEEF]/10">
                <h2 className="text-white font-medium">Conversations</h2>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {directMessages.length === 0 ? (
                  <div className="p-8 text-center">
                    <MessageSquare className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                    <p className="text-[#94A3B8]">No conversations yet</p>
                    <p className="text-[#94A3B8] text-sm">Send a message from the Students tab</p>
                  </div>
                ) : (
                  directMessages.map((thread) => {
                    const hasUnread = thread.messages?.some(m => m.sender === 'student' && !m.read);
                    return (
                      <div
                        key={thread.id}
                        onClick={() => viewMessageThread(thread)}
                        className={`p-4 border-b border-[#00AEEF]/10 cursor-pointer transition-all ${
                          selectedThread?.id === thread.id 
                            ? 'bg-[#00AEEF]/10 border-l-2 border-l-[#00AEEF]' 
                            : 'hover:bg-[#060F22]/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{thread.student_name}</p>
                          {hasUnread && <span className="w-2 h-2 bg-[#FF6A00] rounded-full"></span>}
                        </div>
                        <p className="text-[#00AEEF] text-sm">{thread.subject}</p>
                        <p className="text-[#94A3B8] text-xs mt-1 line-clamp-1">
                          {thread.messages?.[thread.messages.length - 1]?.content}
                        </p>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Message Thread Detail */}
            <div className="lg:col-span-2 bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden flex flex-col">
              {selectedThread ? (
                <>
                  <div className="p-4 border-b border-[#00AEEF]/10">
                    <h2 className="text-white font-bold">{selectedThread.subject}</h2>
                    <p className="text-[#94A3B8] text-sm">with {selectedThread.student_name}</p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[350px]">
                    {selectedThread.messages?.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender === 'teacher' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[80%] ${
                          msg.sender === 'teacher' 
                            ? 'bg-[#FF6A00]/20 border border-[#FF6A00]/30' 
                            : 'bg-[#060F22] border border-[#00AEEF]/10'
                        } rounded-xl p-4`}>
                          <div className="flex items-center gap-2 mb-2">
                            <User className="w-4 h-4 text-[#94A3B8]" />
                            <span className="text-white text-sm font-medium">{msg.sender_name}</span>
                            <span className="text-[#94A3B8] text-xs">{new Date(msg.sent_at).toLocaleString()}</span>
                          </div>
                          <p className="text-white whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <form onSubmit={sendReply} className="p-4 border-t border-[#00AEEF]/10">
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Type your reply..."
                        className="flex-1 bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5"
                      />
                      <button
                        type="submit"
                        disabled={!replyText.trim()}
                        className="bg-[#FF6A00] text-white px-4 py-2.5 rounded-xl hover:bg-[#FF6A00]/80 transition-all disabled:opacity-50 flex items-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        Send
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
                    <p className="text-[#94A3B8]">Select a conversation to view</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            {leaderboard && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#00AEEF]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{leaderboard.total_students}</p>
                      <p className="text-sm text-[#94A3B8]">Total Students</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#00FFE0]/20 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-[#00FFE0]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{leaderboard.students_with_points}</p>
                      <p className="text-sm text-[#94A3B8]">Active Learners</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#FFD700]/20 flex items-center justify-center">
                      <Trophy className="w-5 h-5 text-[#FFD700]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">
                        {leaderboard.leaderboard.length > 0 ? leaderboard.leaderboard[0].name : 'N/A'}
                      </p>
                      <p className="text-sm text-[#94A3B8]">Top Student</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Leaderboard Table */}
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-[#00AEEF]/10">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#FFD700]" />
                  Student Leaderboard
                </h2>
              </div>
              {leaderboard && leaderboard.leaderboard.length > 0 ? (
                <div className="divide-y divide-[#00AEEF]/10">
                  {leaderboard.leaderboard.map((entry) => (
                    <div 
                      key={entry.student_id}
                      className={`flex items-center gap-4 p-4 hover:bg-[#060F22]/50 cursor-pointer ${
                        entry.rank <= 3 ? 'bg-gradient-to-r from-[#FFD700]/5 to-transparent' : ''
                      }`}
                      onClick={() => fetchStudentGamification(entry.student_id)}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        entry.rank === 1 ? 'bg-[#FFD700]/30 text-[#FFD700]' :
                        entry.rank === 2 ? 'bg-gray-400/30 text-gray-300' :
                        entry.rank === 3 ? 'bg-amber-600/30 text-amber-500' :
                        'bg-[#0A1F44] text-[#94A3B8]'
                      }`}>
                        {entry.rank <= 3 ? <Trophy className="w-5 h-5" /> : entry.rank}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-white">{entry.name}</p>
                        <p className="text-xs text-[#94A3B8]">{entry.email}</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium" style={{ color: entry.level?.color || '#00FFE0' }}>
                          {entry.level?.name || 'Beginner'}
                        </p>
                        <p className="text-xs text-[#94A3B8]">Level {entry.level?.level || 1}</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <Medal className="w-4 h-4 text-[#FFD700]" />
                          <span className="text-white font-medium">{entry.badges_count}</span>
                        </div>
                        <p className="text-xs text-[#94A3B8]">Badges</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-white">{entry.total_points.toLocaleString()}</p>
                        <p className="text-xs text-[#94A3B8]">points</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Trophy className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">No students have earned points yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#060F22]">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Student</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Submitted</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Grade</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#00AEEF]/10">
                {submissions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-[#060F22]/50">
                    <td className="px-4 py-3">
                      <p className="text-white font-medium">{sub.student_name}</p>
                      <p className="text-[#94A3B8] text-sm">{sub.student_email}</p>
                    </td>
                    <td className="px-4 py-3 text-[#94A3B8]">{new Date(sub.submitted_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        sub.status === 'graded' ? 'bg-green-500/20 text-green-400' :
                        sub.status === 'needs_revision' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {sub.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white">{sub.grade ? `${sub.grade}/100` : '-'}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => { setSelectedSubmission(sub); setGradeForm({ grade: sub.grade || '', feedback: sub.feedback || '', status: sub.status || 'graded' }); setShowGradeModal(true); }}
                        className="text-[#FF6A00] hover:text-[#FF6A00]/80"
                      >
                        {sub.status === 'graded' ? 'Edit' : 'Grade'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Attendance Tab */}
        {activeTab === 'attendance' && (
          <div className="space-y-6">
            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-6 h-6 text-[#00AEEF]" />
                <h2 className="text-xl font-bold text-white">Today's Attendance</h2>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-[#060F22]/50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-[#00FFE0]">{attendance.filter(a => a.sign_in_time).length}</p>
                  <p className="text-sm text-[#94A3B8]">Present</p>
                </div>
                <div className="bg-[#060F22]/50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-400">{attendance.filter(a => a.sign_in_time && !a.sign_out_time).length}</p>
                  <p className="text-sm text-[#94A3B8]">Signed In</p>
                </div>
                <div className="bg-[#060F22]/50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-red-400">{attendance.filter(a => !a.sign_in_time).length}</p>
                  <p className="text-sm text-[#94A3B8]">Absent</p>
                </div>
              </div>
            </div>

            <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#060F22]">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Student</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Sign In</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Sign Out</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#00AEEF]/10">
                  {attendance.map((a, idx) => (
                    <tr key={idx} className="hover:bg-[#060F22]/50">
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">{a.student_name}</p>
                        <p className="text-[#94A3B8] text-sm">{a.student_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        {a.sign_in_time ? <span className="text-[#00FFE0]">{a.sign_in_time}</span> : <span className="text-[#94A3B8]">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {a.sign_out_time ? <span className="text-[#FF6A00]">{a.sign_out_time}</span> : <span className="text-[#94A3B8]">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          a.status === 'present' ? 'bg-green-500/20 text-green-400' :
                          a.status === 'late' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {a.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {!a.sign_in_time ? (
                          <button onClick={() => signInStudent(a.student_id)} className="flex items-center gap-1 text-sm text-[#00FFE0] hover:text-[#00FFE0]/80">
                            <UserCheck className="w-4 h-4" /> Sign In
                          </button>
                        ) : !a.sign_out_time ? (
                          <button onClick={() => signOutStudent(a.student_id)} className="flex items-center gap-1 text-sm text-[#FF6A00] hover:text-[#FF6A00]/80">
                            <UserX className="w-4 h-4" /> Sign Out
                          </button>
                        ) : (
                          <span className="text-[#94A3B8] text-sm">Complete</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Announcements Tab */}
        {activeTab === 'announcements' && (
          <div className="space-y-6">
            <div className="flex justify-end">
              <button
                onClick={() => setShowAnnouncementModal(true)}
                data-testid="create-announcement-btn"
                className="flex items-center gap-2 bg-[#FF6A00] text-white px-4 py-2 rounded-xl hover:shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all"
              >
                <Plus className="w-4 h-4" /> New Announcement
              </button>
            </div>
            <div className="space-y-4">
              {announcements.map((ann) => (
                <div key={ann.id} className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-bold text-white">{ann.title}</h3>
                        {ann.priority === 'urgent' && <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">Urgent</span>}
                        {ann.priority === 'important' && <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Important</span>}
                      </div>
                      <p className="text-[#94A3B8]">{ann.message}</p>
                      <p className="text-[#94A3B8] text-sm mt-2">{new Date(ann.created_at).toLocaleString()}</p>
                    </div>
                    <button onClick={() => deleteAnnouncement(ann.id)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {announcements.length === 0 && (
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-12 text-center">
                  <Bell className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                  <p className="text-white font-medium">No announcements yet</p>
                  <p className="text-[#94A3B8] text-sm">Create your first announcement to notify students</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Live Classes Tab */}
        {activeTab === 'live-classes' && (
          <div className="space-y-6">
            <div className="flex justify-end">
              <button
                onClick={() => setShowLiveClassModal(true)}
                data-testid="schedule-class-btn"
                className="flex items-center gap-2 bg-[#00AEEF] text-white px-4 py-2 rounded-xl hover:shadow-[0_0_20px_rgba(0,174,239,0.3)] transition-all"
              >
                <Plus className="w-4 h-4" /> Schedule Class
              </button>
            </div>
            <div className="space-y-4">
              {liveClasses.map((lc) => (
                <div key={lc.id} className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Video className="w-5 h-5 text-[#00AEEF]" />
                        <h3 className="text-lg font-bold text-white">{lc.title}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          lc.status === 'scheduled' ? 'bg-blue-500/20 text-blue-400' :
                          lc.status === 'in_progress' ? 'bg-green-500/20 text-green-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {lc.status}
                        </span>
                      </div>
                      <p className="text-[#94A3B8]">{lc.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-[#00FFE0]">{new Date(lc.scheduled_at).toLocaleString()}</span>
                        <span className="text-[#94A3B8]">{lc.duration_minutes} min</span>
                      </div>
                      {lc.meeting_link && (
                        <a href={lc.meeting_link} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-[#00AEEF] hover:text-[#00FFE0]">
                          Join Meeting →
                        </a>
                      )}
                    </div>
                    <button onClick={() => deleteLiveClass(lc.id)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {liveClasses.length === 0 && (
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-12 text-center">
                  <Video className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                  <p className="text-white font-medium">No live classes scheduled</p>
                  <p className="text-[#94A3B8] text-sm">Schedule your first live class</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            {reports ? (
              <>
                {/* Progress Report */}
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#00AEEF]" /> Student Progress Report
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#060F22]">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Student</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Completion</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Assignments</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Avg Grade</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Quiz Avg</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#00AEEF]/10">
                        {reports.progress.students.map((s) => (
                          <tr key={s.student_id} className="hover:bg-[#060F22]/50">
                            <td className="px-4 py-3">
                              <p className="text-white">{s.student_name}</p>
                              <p className="text-[#94A3B8] text-sm">{s.student_email}</p>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-20 h-2 bg-[#060F22] rounded-full overflow-hidden">
                                  <div className="h-full bg-[#00FFE0]" style={{ width: `${s.completion_rate}%` }} />
                                </div>
                                <span className="text-white text-sm">{s.completion_rate}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-white">{s.assignments_graded}/{s.assignments_submitted}</td>
                            <td className="px-4 py-3 text-white">{s.average_grade || '-'}</td>
                            <td className="px-4 py-3 text-white">{s.average_quiz_score || '-'}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Attendance Report */}
                <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl p-6">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#FF6A00]" /> Attendance Report
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#060F22]">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Student</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Present</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Late</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Absent</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-[#94A3B8] uppercase">Rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#00AEEF]/10">
                        {reports.attendance.students.map((s) => (
                          <tr key={s.student_id} className="hover:bg-[#060F22]/50">
                            <td className="px-4 py-3 text-white">{s.student_name}</td>
                            <td className="px-4 py-3 text-[#00FFE0]">{s.present}</td>
                            <td className="px-4 py-3 text-yellow-400">{s.late}</td>
                            <td className="px-4 py-3 text-red-400">{s.absent}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                s.attendance_rate >= 80 ? 'bg-green-500/20 text-green-400' :
                                s.attendance_rate >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {s.attendance_rate}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00AEEF]"></div>
              </div>
            )}
          </div>
        )}

        {/* Announcement Modal */}
        {showAnnouncementModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-lg w-full">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
                <h2 className="text-xl font-bold text-white">New Announcement</h2>
                <button onClick={() => setShowAnnouncementModal(false)} className="text-[#94A3B8] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={createAnnouncement} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Title</label>
                  <input
                    type="text"
                    value={announcementForm.title}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                    required
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5"
                    placeholder="Announcement title"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Message</label>
                  <textarea
                    value={announcementForm.message}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, message: e.target.value })}
                    required
                    rows={4}
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 resize-none"
                    placeholder="Your message to students..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Priority</label>
                  <select
                    value={announcementForm.priority}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, priority: e.target.value })}
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5"
                  >
                    <option value="normal">Normal</option>
                    <option value="important">Important</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowAnnouncementModal(false)} className="flex-1 bg-[#060F22] text-[#94A3B8] px-4 py-2.5 rounded-xl border border-[#00AEEF]/20">
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 bg-[#FF6A00] text-white px-4 py-2.5 rounded-xl">
                    Send Announcement
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Live Class Modal */}
        {showLiveClassModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-lg w-full">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
                <h2 className="text-xl font-bold text-white">Schedule Live Class</h2>
                <button onClick={() => setShowLiveClassModal(false)} className="text-[#94A3B8] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={createLiveClass} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Title</label>
                  <input
                    type="text"
                    value={liveClassForm.title}
                    onChange={(e) => setLiveClassForm({ ...liveClassForm, title: e.target.value })}
                    required
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5"
                    placeholder="Class title"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Description</label>
                  <textarea
                    value={liveClassForm.description}
                    onChange={(e) => setLiveClassForm({ ...liveClassForm, description: e.target.value })}
                    required
                    rows={2}
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 resize-none"
                    placeholder="What will be covered..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#94A3B8] mb-1">Date & Time</label>
                    <input
                      type="datetime-local"
                      value={liveClassForm.scheduled_at}
                      onChange={(e) => setLiveClassForm({ ...liveClassForm, scheduled_at: e.target.value })}
                      required
                      className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#94A3B8] mb-1">Duration (min)</label>
                    <input
                      type="number"
                      value={liveClassForm.duration_minutes}
                      onChange={(e) => setLiveClassForm({ ...liveClassForm, duration_minutes: parseInt(e.target.value) })}
                      required
                      min="15"
                      className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Meeting Link (optional)</label>
                  <input
                    type="url"
                    value={liveClassForm.meeting_link}
                    onChange={(e) => setLiveClassForm({ ...liveClassForm, meeting_link: e.target.value })}
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5"
                    placeholder="https://zoom.us/..."
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowLiveClassModal(false)} className="flex-1 bg-[#060F22] text-[#94A3B8] px-4 py-2.5 rounded-xl border border-[#00AEEF]/20">
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 bg-[#00AEEF] text-white px-4 py-2.5 rounded-xl">
                    Schedule Class
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Grade Modal */}
        {showGradeModal && selectedSubmission && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-lg w-full">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
                <h2 className="text-xl font-bold text-white">Grade Submission</h2>
                <button onClick={() => { setShowGradeModal(false); setSelectedSubmission(null); }} className="text-[#94A3B8] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4 p-4 bg-[#060F22]/50 rounded-xl">
                  <p className="text-white font-medium">{selectedSubmission.student_name}</p>
                  <p className="text-[#94A3B8] text-sm">Submitted: {new Date(selectedSubmission.submitted_at).toLocaleString()}</p>
                  {selectedSubmission.text_content && (
                    <p className="text-[#94A3B8] text-sm mt-2 line-clamp-3">{selectedSubmission.text_content}</p>
                  )}
                  {selectedSubmission.file && (
                    <p className="text-[#00AEEF] text-sm mt-2">📎 {selectedSubmission.file.filename}</p>
                  )}
                </div>
                <form onSubmit={gradeSubmission} className="space-y-4">
                  <div>
                    <label className="block text-sm text-[#94A3B8] mb-1">Grade (0-100)</label>
                    <input
                      type="number"
                      value={gradeForm.grade}
                      onChange={(e) => setGradeForm({ ...gradeForm, grade: e.target.value })}
                      required
                      min="0"
                      max="100"
                      className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#94A3B8] mb-1">Feedback</label>
                    <textarea
                      value={gradeForm.feedback}
                      onChange={(e) => setGradeForm({ ...gradeForm, feedback: e.target.value })}
                      required
                      rows={3}
                      className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 resize-none"
                      placeholder="Your feedback for the student..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#94A3B8] mb-1">Status</label>
                    <select
                      value={gradeForm.status}
                      onChange={(e) => setGradeForm({ ...gradeForm, status: e.target.value })}
                      className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5"
                    >
                      <option value="graded">Graded</option>
                      <option value="needs_revision">Needs Revision</option>
                    </select>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button type="button" onClick={() => { setShowGradeModal(false); setSelectedSubmission(null); }} className="flex-1 bg-[#060F22] text-[#94A3B8] px-4 py-2.5 rounded-xl border border-[#00AEEF]/20">
                      Cancel
                    </button>
                    <button type="submit" className="flex-1 bg-[#00FFE0] text-[#0A1F44] font-medium px-4 py-2.5 rounded-xl">
                      Save Grade
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Direct Message Modal */}
        {showMessageModal && selectedStudent && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-lg w-full">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
                <div>
                  <h2 className="text-xl font-bold text-white">Send Message</h2>
                  <p className="text-[#94A3B8] text-sm">to {selectedStudent.student.name}</p>
                </div>
                <button onClick={() => { setShowMessageModal(false); setSelectedStudent(null); }} className="text-[#94A3B8] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={sendDirectMessage} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Subject</label>
                  <input
                    type="text"
                    value={messageForm.subject}
                    onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                    required
                    data-testid="message-subject-input"
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5"
                    placeholder="Message subject"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-1">Message</label>
                  <textarea
                    value={messageForm.message}
                    onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                    required
                    rows={5}
                    data-testid="message-content-input"
                    className="w-full bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 resize-none"
                    placeholder="Your message..."
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => { setShowMessageModal(false); setSelectedStudent(null); }} className="flex-1 bg-[#060F22] text-[#94A3B8] px-4 py-2.5 rounded-xl border border-[#00AEEF]/20">
                    Cancel
                  </button>
                  <button type="submit" data-testid="send-message-btn" className="flex-1 bg-[#00AEEF] text-white px-4 py-2.5 rounded-xl flex items-center justify-center gap-2">
                    <Send className="w-4 h-4" />
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Student Gamification Modal */}
        {showGamificationModal && selectedStudentGamification && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A1F44] border border-[#00AEEF]/20 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-[#00AEEF]/20">
                <div>
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[#FFD700]" />
                    {selectedStudentGamification.student_name}'s Achievements
                  </h2>
                  <p className="text-[#94A3B8] text-sm">{selectedStudentGamification.student_email}</p>
                </div>
                <button onClick={() => { setShowGamificationModal(false); setSelectedStudentGamification(null); }} className="text-[#94A3B8] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                {/* Level & Points */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#060F22] rounded-xl p-4 text-center">
                    <div className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center"
                      style={{ backgroundColor: `${selectedStudentGamification.level?.color}30` }}>
                      <Star className="w-6 h-6" style={{ color: selectedStudentGamification.level?.color }} />
                    </div>
                    <p className="text-lg font-bold" style={{ color: selectedStudentGamification.level?.color }}>
                      {selectedStudentGamification.level?.name}
                    </p>
                    <p className="text-xs text-[#94A3B8]">Level {selectedStudentGamification.level?.level}</p>
                  </div>
                  <div className="bg-[#060F22] rounded-xl p-4 text-center">
                    <div className="flex items-center justify-center gap-1 mb-2">
                      <Zap className="w-6 h-6 text-[#FF6A00]" />
                    </div>
                    <p className="text-2xl font-bold text-white">{selectedStudentGamification.total_points}</p>
                    <p className="text-xs text-[#94A3B8]">Total Points</p>
                  </div>
                  <div className="bg-[#060F22] rounded-xl p-4 text-center">
                    <div className="flex items-center justify-center gap-1 mb-2">
                      <Trophy className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <p className="text-2xl font-bold text-white">#{selectedStudentGamification.rank}</p>
                    <p className="text-xs text-[#94A3B8]">Leaderboard Rank</p>
                  </div>
                </div>

                {/* Badges */}
                <div>
                  <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Medal className="w-4 h-4 text-[#FFD700]" />
                    Badges ({selectedStudentGamification.badges_earned}/{selectedStudentGamification.badges_total})
                  </h3>
                  <div className="grid grid-cols-4 gap-2">
                    {selectedStudentGamification.badges.filter(b => b.earned).map((badge) => (
                      <div key={badge.id} className="bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-lg p-2 text-center">
                        <Trophy className="w-6 h-6 text-[#FFD700] mx-auto mb-1" />
                        <p className="text-xs text-white font-medium truncate">{badge.name}</p>
                      </div>
                    ))}
                    {selectedStudentGamification.badges.filter(b => b.earned).length === 0 && (
                      <p className="text-[#94A3B8] text-sm col-span-4">No badges earned yet</p>
                    )}
                  </div>
                </div>

                {/* Recent Activity */}
                <div>
                  <h3 className="text-white font-medium mb-3">Recent Activity</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedStudentGamification.recent_activity.map((activity) => (
                      <div key={activity.id} className="flex items-center justify-between bg-[#060F22] rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-[#00FFE0]" />
                          <span className="text-sm text-[#94A3B8]">{activity.description || activity.action}</span>
                        </div>
                        <span className="text-[#00FFE0] font-medium">+{activity.points}</span>
                      </div>
                    ))}
                    {selectedStudentGamification.recent_activity.length === 0 && (
                      <p className="text-[#94A3B8] text-sm">No recent activity</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherDashboard;
